import React from 'react';
import { X, User, Calendar, BookOpen, AlertCircle, Zap, Activity } from 'lucide-react';

function PaperDetailModal({ isOpen, onClose, paperData, loading }) {
    if (!isOpen) return null;

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000]">
                <div className="bg-white p-8 rounded-xl shadow-2xl flex flex-col items-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading Paper Profile...</p>
                </div>
            </div>
        );
    }

    if (!paperData) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden animate-slide-up transform transition-all">
                
                {/* Header */}
                <div className="flex justify-between items-center p-5 border-b border-gray-100 bg-white">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Paper Analysis</span>
                    <button 
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div className="overflow-y-auto p-6 space-y-6">
                    
                    {/* Hero Section */}
                    <div>
                        <div className="flex gap-2 mb-3">
                            <span className="px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold rounded-full uppercase tracking-wide">
                                Paper
                            </span>
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-full uppercase tracking-wide">
                                {paperData.venue || 'Preprint'}
                            </span>
                        </div>
                        
                        <h2 className="text-2xl font-bold text-gray-900 leading-tight mb-4">
                            {paperData.title}
                        </h2>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 pb-6 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                                <User size={16} className="text-gray-400" />
                                <span className="font-medium">
                                    {paperData.authors && paperData.authors.length > 0 
                                        ? paperData.authors.join(', ') 
                                        : 'Unknown Authors'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar size={16} className="text-gray-400" />
                                <span>{paperData.publication_date || 'Date N/A'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Abstract */}
                    <div>
                        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 flex items-center gap-2">
                            <BookOpen size={14} className="text-blue-600" />
                            Abstract
                        </h3>
                        <p className="text-sm leading-relaxed text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-100">
                            {paperData.abstract || 'No abstract available.'}
                        </p>
                    </div>

                    {/* Analysis Grid */}
                    <div>
                        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Analysis Summary</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            
                            {/* Problem Card */}
                            <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-2 text-blue-700 font-bold text-xs uppercase tracking-wide">
                                    <AlertCircle size={14} />
                                    Problem
                                </div>
                                <p className="text-xs leading-relaxed text-gray-700">
                                    {paperData.problem_summary || 'N/A'}
                                </p>
                            </div>

                            {/* Method Card */}
                            <div className="bg-green-50/50 border border-green-100 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-2 text-green-700 font-bold text-xs uppercase tracking-wide">
                                    <Zap size={14} />
                                    Method
                                </div>
                                <p className="text-xs leading-relaxed text-gray-700">
                                    {paperData.method_summary || 'N/A'}
                                </p>
                            </div>

                            {/* Experiment Card */}
                            <div className="bg-purple-50/50 border border-purple-100 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-2 text-purple-700 font-bold text-xs uppercase tracking-wide">
                                    <Activity size={14} />
                                    Experiment
                                </div>
                                <p className="text-xs leading-relaxed text-gray-700">
                                    {paperData.experiment_summary || 'N/A'}
                                </p>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default PaperDetailModal;